import OCGatewayClient from "../client/OCGatewayClient";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { EventSummary } from "../models/EventSummary";
import { gatewayRealtimeQueryOptions } from "./gatewayQueryUtils";

let subscribed = false;

export function useEventSummaryQuery(deviceId: string, eventId: number, accessToken?: string | null) {
    const queryClient = useQueryClient();

    const query = useQuery({
        // token appended conditionally rather than filter(Boolean), which would
        // also drop numeric falsy parts like an eventId of 0
        queryKey: ['getEventSummary', deviceId, eventId, ...(accessToken ? [accessToken] : [])],
        enabled: !!deviceId && Number.isFinite(eventId),
        queryFn: async (): Promise<EventSummary | null> => {
            const response = await OCGatewayClient.request('getEventSummary', {
                deviceId,
                eventId,
                subscribe: true,
                accessToken
            }) as Promise<EventSummary | null>;

            return response ? new EventSummary(response) : null;
        },
        ...gatewayRealtimeQueryOptions
    });

    if (!subscribed) {
        OCGatewayClient.socket.on('eventSummaryUpdate', (data: any) => {
            const body = data?.body ?? data?.summary ?? data;
            const updateDeviceId = data?.deviceId ?? body?.deviceId;
            const updateEventId = data?.eventId ?? body?.eventId;

            if (!updateDeviceId || !Number.isFinite(updateEventId)) {
                console.log('eventSummaryUpdate (ignored)', data);
                return;
            }

            queryClient.setQueriesData(
                {
                    queryKey: ['getEventSummary', updateDeviceId, updateEventId],
                    exact: false
                },
                (eventSummary: EventSummary | null | undefined) => {
                    if (body === null || data?.type === 'delete') {
                        console.log('eventSummaryUpdate (relevant delete)', data);
                        return null;
                    }

                    if (!body || typeof body !== 'object') {
                        console.log('eventSummaryUpdate (ignored body)', data);
                        return eventSummary ?? null;
                    }

                    if (!eventSummary) {
                        console.log('eventSummaryUpdate (relevant create)', data);
                        return new EventSummary(body);
                    }

                    console.log('eventSummaryUpdate (relevant update)', data);
                    return new EventSummary({
                        ...eventSummary,
                        ...body,
                        subevents: Array.isArray(body.subevents) ? body.subevents : eventSummary.subevents
                    });
                }
            );
        });

        subscribed = true;
    }

    return query;
}
